/**
 * Pure helpers for AssetTypePicker — search + drill-down.
 *
 * The picker walks the asset_type inheritance tree by `inherits_from_asset_type_uid`.
 * Helpers here are extracted so tree logic stays unit-testable; the React
 * component is a thin shell around `Command` + `Popover`/`Sheet`.
 */

import type { AssetTypeMeta } from '../equipment/add-equipment'
import { assetTypeLabel, childTypesOf } from '../equipment/add-equipment'

export type { AssetTypeMeta }
export { assetTypeLabel, childTypesOf }

/**
 * Walk from `uid` up to the root via `inherits_from_asset_type_uid`,
 * returning the chain ordered root → … → leaf. Empty when uid is missing.
 *
 * Cycles (which would indicate corrupt provisioning data) are guarded by a
 * visited-set so the walk terminates regardless.
 */
export function buildPath(allTypes: AssetTypeMeta[], uid: string | null | undefined): AssetTypeMeta[] {
  if (!uid) return []
  const byUid = new Map<string, AssetTypeMeta>()
  for (const t of allTypes) byUid.set(t.uid, t)
  const out: AssetTypeMeta[] = []
  const visited = new Set<string>()
  let cursor: string | null | undefined = uid
  while (cursor && !visited.has(cursor)) {
    const node = byUid.get(cursor)
    if (!node) break
    visited.add(cursor)
    out.push(node)
    cursor = node.inherits_from_asset_type_uid ?? null
  }
  out.reverse()
  return out
}

/** Lower-cased substring match against the localized label and the code. */
function matchesQuery(at: AssetTypeMeta, query: string, locale: string): boolean {
  if (!query) return true
  const q = query.toLowerCase().trim()
  if (q === '') return true
  const label = assetTypeLabel(at, locale).toLowerCase()
  if (label.includes(q)) return true
  return at.code.toLowerCase().includes(q)
}

/**
 * Children of `parentUid` whose label matches `query`. When `parentUid` is
 * null/empty, returns root types (those with no `inherits_from_asset_type_uid`).
 */
export function searchAtLevel(
  allTypes: AssetTypeMeta[],
  parentUid: string | null,
  query: string,
  locale: string,
): AssetTypeMeta[] {
  const children = parentUid
    ? childTypesOf(allTypes, parentUid)
    : allTypes
        .filter((at) => !at.inherits_from_asset_type_uid)
        .slice()
        .sort((a, b) => {
          const ao = a.ordering ?? Number.POSITIVE_INFINITY
          const bo = b.ordering ?? Number.POSITIVE_INFINITY
          if (ao !== bo) return ao - bo
          return a.code.localeCompare(b.code)
        })
  if (!query) return children
  return children.filter((at) => matchesQuery(at, query, locale))
}

export interface GlobalMatch {
  type: AssetTypeMeta
  /** Ancestor chain (root → leaf), excluding the matched type itself. */
  path: AssetTypeMeta[]
}

/**
 * Search every type in the tree whose label matches `query`. Each result is
 * paired with its ancestor chain so the caller can render breadcrumbs alongside
 * the match. Optionally restricted to descendants of `restrictToDescendantsOf`.
 */
export function searchGlobal(
  allTypes: AssetTypeMeta[],
  query: string,
  locale: string,
  restrictToDescendantsOf?: string,
): GlobalMatch[] {
  if (!query.trim()) return []
  const results: GlobalMatch[] = []
  for (const at of allTypes) {
    if (!matchesQuery(at, query, locale)) continue
    const fullPath = buildPath(allTypes, at.uid)
    if (restrictToDescendantsOf) {
      const isDescendant = fullPath.some((node) => node.uid === restrictToDescendantsOf)
      if (!isDescendant) continue
      // Drop everything up to and including the restriction root from the breadcrumb.
      const idx = fullPath.findIndex((node) => node.uid === restrictToDescendantsOf)
      const trimmed = idx >= 0 ? fullPath.slice(idx + 1, -1) : fullPath.slice(0, -1)
      results.push({ type: at, path: trimmed })
      continue
    }
    results.push({ type: at, path: fullPath.slice(0, -1) })
  }
  results.sort((a, b) => assetTypeLabel(a.type, locale).localeCompare(assetTypeLabel(b.type, locale)))
  return results
}

/**
 * Has the given type any children at all? Drives the "drill into" affordance
 * on each row of the picker.
 */
export function hasChildren(allTypes: AssetTypeMeta[], uid: string): boolean {
  for (const at of allTypes) {
    if (at.inherits_from_asset_type_uid === uid) return true
  }
  return false
}

/**
 * The set of root types the picker should show at the top level. When
 * `restrictToDescendantsOf` is given, the "root" is that type's direct
 * children (so e.g. AddEquipmentDialog opens with the trades list).
 */
export function topLevelTypes(
  allTypes: AssetTypeMeta[],
  restrictToDescendantsOf?: string,
): AssetTypeMeta[] {
  if (restrictToDescendantsOf) {
    return childTypesOf(allTypes, restrictToDescendantsOf)
  }
  return allTypes
    .filter((at) => !at.inherits_from_asset_type_uid)
    .slice()
    .sort((a, b) => {
      const ao = a.ordering ?? Number.POSITIVE_INFINITY
      const bo = b.ordering ?? Number.POSITIVE_INFINITY
      if (ao !== bo) return ao - bo
      return a.code.localeCompare(b.code)
    })
}
